import React, { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/custom/button";
import {
  client,
  CreateOperationalHoursDto,
  SiteGroupSummaryDto,
} from "@/client";
import { toast } from "sonner";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import { SiteGroupsTableContext } from "./-site-groups-table-context";
import { useFinalQuery } from "@/hooks/use-finalQuery";
import { useResolveAllSelectedIds } from "@/hooks/use-resolve-all-selected-ids";
import {
  OperationalHoursEditor,
  OperationalHoursEditorRef,
} from "@/components/custom/operational-hours-editor";
import { useOperationalHoursDefaults } from "@/hooks/use-operational-hours-defaults";
import { Loader } from "@/components/custom/loader";
import { UploadCatalogDialog } from "../../data/catalog/-upload-catalog-dialog";

import { showActionErrorToast } from "@/state/error-toast";

export const DeleteAction: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SiteGroupsTableContext.useTable();

  const { finalQuery, count } = useFinalQuery({
    context: SiteGroupsTableContext,
    idsKey: "siteGroupIds",
    convert: (id) => parseInt(id, 10),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      let siteGroupIds = finalQuery.siteGroupIds ?? [];

      // For "select all" with filters, resolve IDs from all matched pages.
      if (!siteGroupIds.length && finalQuery.name) {
        const pageSize = 100;
        let page = 1;
        let totalPages = 1;
        const collectedIds: number[] = [];

        while (page <= totalPages) {
          const response = await client.siteGroups.listSummaries({
            ...finalQuery,
            page,
            limit: pageSize,
          });

          const ids = ((response.results ?? []) as SiteGroupSummaryDto[])
            .map((group) => group.id)
            .filter((id): id is number => id !== undefined);

          collectedIds.push(...ids);
          totalPages = Math.max(1, Math.ceil((response.count ?? 0) / pageSize));
          page += 1;
        }

        siteGroupIds = collectedIds;
      }

      if (!siteGroupIds.length) {
        return;
      }

      await Promise.all(
        siteGroupIds.map((id) => client.siteGroups.delete.assign({ id })({})),
      );
    },
    onError: (err: any) => showActionErrorToast(err, t("deleteFailed")),
    onSuccess: () => {
      toast.success(t("siteGroupsDeleteSuccess"));
      queryClient.invalidateQueries({
        queryKey: client.siteGroups.listSummaries.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: client.sites.listSummaries.queryKey,
      });
      table.setAllSelected(false);
      table.resetRowSelection();
    },
  });

  const onSubmit = () => {
    deleteMutation.mutate();
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("confirmDeleteSiteGroups", { count })}
      </p>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">{t("cancel")}</Button>
        </DialogClose>
        <Button
          onClick={onSubmit}
          disabled={count === 0 || deleteMutation.isPending}
          variant="destructive"
        >
          {deleteMutation.isPending ? t("deleting") : t("delete")}
        </Button>
      </DialogFooter>
    </div>
  );
};

export const OperationalHoursAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const table = SiteGroupsTableContext.useTable();
  const editorRef = useRef<OperationalHoursEditorRef>(null);
  const isAllSelected = table.getAllSelected();

  const selectedIds = table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original.id!);

  const { finalQuery, count } = useFinalQuery({
    context: SiteGroupsTableContext,
    idsKey: "siteGroupIds",
    convert: (id) => parseInt(id, 10),
  });

  const { defaultValue, isLoading: defaultsLoading } =
    useOperationalHoursDefaults({
      selectedIds,
      isAllSelected,
      mode: "siteGroups",
    });

  const upsertBulk = useMutation({
    ...client.siteGroups.upsertOperationalHoursBulk.asMutationOptions(),
    onError: (err: any) =>
      showActionErrorToast(err, t("operationalHoursSaveFailed")),
    onSuccess: () => {
      toast.success(t("operationalHoursBulkUpdated", { count }));
      queryClient.invalidateQueries({
        queryKey: client.siteGroups.listSummaries.queryKey,
      });
      table.setAllSelected(false);
      table.resetRowSelection();
      onClose();
    },
  });

  const onSave = async (data: CreateOperationalHoursDto) => {
    await upsertBulk.mutateAsync({
      requestBody: {
        ...data,
        query: finalQuery,
      },
    });
  };

  if (defaultsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("operationalHoursBulkDescription", { count })}
      </p>
      <OperationalHoursEditor
        ref={editorRef}
        value={defaultValue}
        onSave={onSave}
        isEditing={true}
      />
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">{t("cancel")}</Button>
        </DialogClose>
        <Button
          variant="azure"
          disabled={count === 0 || upsertBulk.isPending}
          onClick={() => editorRef.current?.submit()}
        >
          {upsertBulk.isPending ? t("saving") : t("save")}
        </Button>
      </DialogFooter>
    </div>
  );
};

export const UploadCatalogAction: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const table = SiteGroupsTableContext.useTable();
  const isAllSelected = table.getAllSelected();

  const { finalQuery } = useFinalQuery({
    context: SiteGroupsTableContext,
    idsKey: "siteGroupIds",
    convert: (id) => parseInt(id, 10),
  });

  const {
    ids: groupIds,
    isLoading,
    error,
  } = useResolveAllSelectedIds({
    isAllSelected,
    selectedIds: finalQuery.siteGroupIds ?? [],
    finalQuery,
    listSummaries: (params) => client.siteGroups.listSummaries(params),
  });

  // Require at least one site group to be selected (including when "select all" resolves to none)
  if (groupIds !== null && groupIds.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("Please select at least one site group to upload a catalog.")}
        </p>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("close")}</Button>
          </DialogClose>
        </DialogFooter>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">{error}</p>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("close")}</Button>
          </DialogClose>
        </DialogFooter>
      </div>
    );
  }

  if (isLoading || groupIds === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader size="md" />
      </div>
    );
  }

  return <UploadCatalogDialog onClose={onClose} initialGroupIds={groupIds} />;
};
